import React, { useState } from "react";
import format from "lib/format";
import Avatar from "Components/Base/DataDisplay/Avatar";
import styled from "styled-components";
import { getNameInitials } from "lib/utils";

const StyledAvatar = styled(Avatar)`
  background-color: #ccc;
`;

/**
 * A profile avatar component that displays a user's image with automatic fallback to initials.
 * Fetches the avatar image using a personal ID and displays initials derived from the title
 * if the image fails to load or is unavailable.
 *
 * @component
 * @example
 * // Basic usage with personal ID
 * <ProfileAvatar
 *   size="medium"
 *   personalId="12345"
 *   title="John Doe"
 * />
 *
 * @example
 * // With custom styling and hidden title on hover
 * <ProfileAvatar
 *   size="large"
 *   personalId="67890"
 *   title="Jane Smith"
 *   alt="Profile picture of Jane Smith"
 *   className="custom-avatar"
 *   hideTitle={true}
 * />
 *
 * @param {Object} props - The component props
 * @param {string} props.size - The size of the avatar (e.g., "small", "medium", "large")
 * @param {string} props.personalId - Personal ID used to fetch the avatar image from the server
 * @param {string} [props.title] - The name or title of the person, used for generating initials fallback
 * @param {string} [props.alt] - Alternative text for the avatar image for accessibility
 * @param {string} [props.className] - Additional CSS class name for custom styling
 * @param {boolean} [props.hideTitle=false] - When true, prevents the title attribute from being rendered on hover
 *
 * @returns {React.ReactElement} The rendered ProfileAvatar component
 *
 * @see {@link Components/Base/DataDisplay/Avatar} Base Avatar component
 */

const ProfileAvatar = ({
  size,
  personalId,
  title,
  alt,
  className,
  hideTitle = false,
}) => {
  const [imgSrc, setImgSrc] = useState(
    format.url(
      `/Personal/StreamImage.aspx?pres=1&pid=${personalId}&size=${size}&ddi=true`,
    ),
  );

  const initials = getNameInitials(title);

  const handleError = () => {
    setImgSrc(null);
  };

  return (
    <StyledAvatar
      size={size}
      title={hideTitle ? null : title}
      alt={alt ?? title}
      className={className}
      src={imgSrc}
      onError={handleError}
      label={initials}
    />
  );
};

export default ProfileAvatar;
